import { useNavigate } from "react-router-dom";
import SEO from "../components/seo/SEO";
import { Section, Fade, Heading, Label, Text, Button, C } from "../components/ui";

export default function Portfolio() {
  const navigate = useNavigate();

  const goToContact = () => {
    navigate("/contact");
  };

  const portfolioItems = [
    {
      category: "Social Media",
      title: "Restaurant Instagram Growth",
      desc: "Built a restaurant's Instagram from 0 to 2,200+ followers in 3 months with engaging content and local targeting.",
      metrics: ["2,200+ followers", "3× engagement", "40% more bookings"],
      image: "🍽️",
    },
    {
      category: "Web Development",
      title: "Salon Website Redesign",
      desc: "Created a beautiful, conversion-focused website for a luxury salon with online booking integration.",
      metrics: ["60% more bookings", "4.9★ Google rating", "Fully booked 6 weeks out"],
      image: "💇",
    },
    {
      category: "Branding",
      title: "Barbershop Brand Identity",
      desc: "Developed complete brand identity for a new barbershop location including logo, colors, and messaging.",
      metrics: ["#1 on Google Maps", "25 clients in month 1", "80+ 5★ reviews"],
      image: "💈",
    },
    {
      category: "Marketing Automation",
      title: "Email Automation Setup",
      desc: "Implemented automated email sequences and CRM integration for a service business.",
      metrics: ["400+ email list", "Automated follow-ups", "No more lost leads"],
      image: "📧",
    },
    {
      category: "Content Creation",
      title: "Short-Form Video Series",
      desc: "Produced 16 short-form videos per month showcasing transformations and behind-the-scenes content.",
      metrics: ["1,800 new followers", "Viral Reels", "Massive engagement"],
      image: "🎬",
    },
    {
      category: "Paid Advertising",
      title: "Local Meta Ads Campaign",
      desc: "Launched targeted Facebook and Instagram ads for local business within 10km radius.",
      metrics: ["3× profile views", "High ROI", "Consistent leads"],
      image: "📱",
    },
  ];

  return (
    <>
      <SEO
        title="Portfolio"
        description="See our work: real results for real local businesses. From social media to web development, view our portfolio of successful projects."
      />

      {/* Hero Section */}
      <Section background={C.navy}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto" }}>
            <Label light>Portfolio</Label>
            <Heading light style={{ marginBottom: "24px" }}>
              Real Work, Real Results
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              Browse through our recent projects and see how we've helped local businesses
              across Canada achieve remarkable growth.
            </Text>
            <Button variant="coral" onClick={goToContact}>
              Start Your Project
            </Button>
          </div>
        </Fade>
      </Section>

      {/* Portfolio Grid */}
      <Section background={C.offWhite}>
        <Fade>
          <div style={{ textAlign: "center", marginBottom: "60px" }}>
            <Label>Our Work</Label>
            <Heading size={2}>Featured Projects</Heading>
          </div>
        </Fade>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: "32px",
          }}
        >
          {portfolioItems.map((item, index) => (
            <Fade key={index} delay={index * 0.1}>
              <div
                style={{
                  background: C.white,
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06)",
                  transition: "all 0.3s ease",
                }}
              >
                {/* Image Placeholder */}
                <div
                  style={{
                    background: C.gray100,
                    height: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "4rem",
                  }}
                >
                  {item.image}
                </div>

                {/* Content */}
                <div style={{ padding: "24px" }}>
                  <div
                    style={{
                      fontFamily: C.mono,
                      fontSize: "11px",
                      color: C.coral,
                      fontWeight: 600,
                      letterSpacing: "0.1em",
                      marginBottom: "12px",
                      textTransform: "uppercase",
                    }}
                  >
                    {item.category}
                  </div>
                  <Heading
                    size={3}
                    style={{ marginBottom: "12px", fontSize: "1.25rem" }}
                  >
                    {item.title}
                  </Heading>
                  <Text
                    size="sm"
                    style={{ color: C.gray600, marginBottom: "20px", lineHeight: 1.6 }}
                  >
                    {item.desc}
                  </Text>

                  {/* Metrics */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginBottom: "16px",
                    }}
                  >
                    {item.metrics.map((metric, idx) => (
                      <span
                        key={idx}
                        style={{
                          background: C.gray50,
                          padding: "6px 12px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          color: C.gray600,
                          fontWeight: 500,
                        }}
                      >
                        {metric}
                      </span>
                    ))}
                  </div>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={goToContact}
                    style={{ width: "100%" }}
                  >
                    Get Similar Results
                  </Button>
                </div>
              </div>
            </Fade>
          ))}
        </div>
      </Section>

      {/* CTA Section */}
      <Section background={C.navyLight}>
        <Fade>
          <div style={{ textAlign: "center", maxWidth: "700px", margin: "0 auto" }}>
            <Heading light style={{ marginBottom: "24px" }}>
              Want Results Like These?
            </Heading>
            <Text light size="lg" style={{ marginBottom: "40px" }}>
              Let's discuss your project and create a custom strategy for your business.
            </Text>
            <Button variant="coral" size="lg" onClick={goToContact}>
              Get Your Free Consultation
            </Button>
          </div>
        </Fade>
      </Section>
    </>
  );
}
